import { useEffect, useState } from "react";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Check, X, Calendar, Instagram } from "lucide-react";

const CONTENT_STATUS = { DRAFT: "#94A3B8", SUBMITTED: "#60A5FA", APPROVED: "#34D399", SCHEDULED: "#A78BFA", PUBLISHED: "#10B981", REJECTED: "#6B7280" };

export default function ContentHub() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [f, setF] = useState({ project_name: "", caption: "", tags: "", permission_confirmed: false });

  const load = () => api.get("/content").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!f.project_name) return toast.error("Project name required");
    if (!f.permission_confirmed) return toast.error("Please confirm publishing permission");
    const fd = new FormData();
    fd.append("project_name", f.project_name); fd.append("caption", f.caption); fd.append("tags", f.tags);
    fd.append("permission_confirmed", f.permission_confirmed);
    if (file) fd.append("file", file);
    try { await api.post("/content", fd); toast.success("Submitted for 3B review"); setOpen(false); setFile(null); setF({ project_name: "", caption: "", tags: "", permission_confirmed: false }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status, schedule_date = null) => {
    try { await api.patch(`/content/${id}`, { status, schedule_date }); toast.success(`Content ${status.toLowerCase()}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const inputCls = "w-full panel rounded-sm px-3 py-2.5 text-sm outline-none focus:border-[#C5A059]";

  return (
    <div>
      <PageHeader eyebrow="Approval Workflow" title="3B Content Hub">
        {!isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><button data-testid="submit-content-btn" className="btn-brass flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-sm"><Upload size={15} /> Submit Content</button></DialogTrigger>
            <DialogContent className="bg-[#131316] border-[#28282D] text-[#F5F3EF]">
              <DialogHeader><DialogTitle className="font-serif text-2xl">Submit Content</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div><label className="eyebrow block mb-1.5">Photo / Video</label><input type="file" accept="image/*,video/*" onChange={(e) => setFile(e.target.files[0])} data-testid="content-file" className={inputCls} /></div>
                <div><label className="eyebrow block mb-1.5">Piece / Project Name</label><input data-testid="content-name" value={f.project_name} onChange={(e) => setF((p) => ({ ...p, project_name: e.target.value }))} className={inputCls} /></div>
                <div><label className="eyebrow block mb-1.5">Caption Suggestion</label><textarea value={f.caption} onChange={(e) => setF((p) => ({ ...p, caption: e.target.value }))} rows={3} className={inputCls} /></div>
                <div><label className="eyebrow block mb-1.5">Tags (comma separated)</label><input value={f.tags} onChange={(e) => setF((p) => ({ ...p, tags: e.target.value }))} className={inputCls} placeholder="signet, goldsmith" /></div>
                <label className="flex items-center gap-2.5 text-sm text-[#C2BEB5] cursor-pointer">
                  <input type="checkbox" checked={f.permission_confirmed} onChange={(e) => setF((p) => ({ ...p, permission_confirmed: e.target.checked }))} data-testid="content-permission" className="accent-[#D4AF37] w-4 h-4" />
                  I confirm 3B may publish this content.
                </label>
                <button onClick={submit} data-testid="submit-content" className="btn-brass w-full py-2.5 rounded-sm text-sm">Submit for Review</button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="panel rounded-sm p-4 mb-5 flex items-center gap-3 text-xs text-[#8C867A]" style={{ borderColor: "#3E382A" }}>
        <Instagram size={16} className="brass-text shrink-0" />
        <span>Bench holders submit → 3B reviews → approves → schedules / publishes. Direct social publishing (Instagram, TikTok, YouTube) activates once platform credentials are configured.</span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.length === 0 && <div className="text-sm text-[#8C867A] col-span-full py-10 text-center">No content submissions yet.</div>}
        {items.map((c) => (
          <div key={c.id} data-testid={`content-${c.id}`} className="panel rounded-sm p-5">
            <div className="flex items-start justify-between mb-2">
              <div className="font-serif text-lg text-[#F5F3EF] leading-snug">{c.project_name}</div>
              <span className="text-[0.6rem] uppercase tracking-wider px-2 py-1 rounded-sm shrink-0" style={{ color: CONTENT_STATUS[c.status], background: `${CONTENT_STATUS[c.status]}18` }}>{c.status}</span>
            </div>
            {c.caption && <p className="text-xs text-[#C2BEB5] mb-2 line-clamp-2">{c.caption}</p>}
            <div className="flex flex-wrap gap-1 mb-3">{(c.tags || []).map((t) => <span key={t} className="text-[0.6rem] text-[#8C867A]">#{t}</span>)}</div>
            <div className="text-[0.65rem] text-[#8C867A] pt-2 border-t hairline">{c.submitted_by} · {c.bench_number} · {fmtDateTime(c.created_at)}</div>
            {isAdmin && c.status === "SUBMITTED" && (
              <div className="flex gap-2 mt-3">
                <button onClick={() => setStatus(c.id, "APPROVED")} data-testid={`approve-${c.id}`} className="btn-brass flex-1 py-2 rounded-sm text-xs flex items-center justify-center gap-1"><Check size={13} /> Approve</button>
                <button onClick={() => setStatus(c.id, "REJECTED")} data-testid={`reject-${c.id}`} className="panel flex-1 py-2 rounded-sm text-xs text-[#C2BEB5] hover:border-[#F59E0B] transition-colors flex items-center justify-center gap-1"><X size={13} /> Reject</button>
              </div>
            )}
            {isAdmin && c.status === "APPROVED" && (
              <button onClick={() => setStatus(c.id, "SCHEDULED", new Date(Date.now() + 86400000).toISOString())} data-testid={`schedule-${c.id}`}
                className="btn-brass w-full py-2 rounded-sm text-xs flex items-center justify-center gap-1 mt-3"><Calendar size={13} /> Schedule</button>
            )}
            {isAdmin && c.status === "SCHEDULED" && (
              <button onClick={() => setStatus(c.id, "PUBLISHED")} data-testid={`publish-${c.id}`} className="btn-brass w-full py-2 rounded-sm text-xs mt-3">Mark Published</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
